import React, { useState, useEffect, useMemo } from "react";
import {
  SparklesIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/solid";
import { getCachedInsight } from "../../services/aiInsightsFunctions";
import { useAuth } from "../../contexts/AuthContext";
import { UserService } from "../../services/userService";
import { getPersonalizedInsightsEnabled } from "../../hooks/usePersonalizedInsights";

import { generateInsights } from "../../services/aiInsightsFunctions";
import { safeGetItem, safeSetItem } from "../../utils/localStorageUtils";

interface DashboardAIInsightCardProps {
  insightType: "wellness" | "peak" | "sleep" | "training" | "daily";
  timeframe: "Day" | "Week" | "Month";
  dateId: string; // Required for dismiss functionality
  onLearnMore?: () => void; // Optional learn more functionality
  learnMoreLoading?: boolean;
  className?: string;
  hasRequiredData?: boolean; // Optional prop to indicate if required data exists
  onDismiss?: () => void; // Optional callback when insight is dismissed
  forceShow?: boolean; // When true, overrides dismissed state to show the card
}

const DashboardAIInsightCard: React.FC<DashboardAIInsightCardProps> = ({
  insightType,
  timeframe,
  dateId,
  onLearnMore,
  learnMoreLoading = false,
  className = "",
  hasRequiredData = true, // Default to true for backward compatibility
  onDismiss,
  forceShow = false,
}) => {
  const { currentUser } = useAuth();
  const [insight, setInsight] = useState<string>("");
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [insightsEnabled, setInsightsEnabled] = useState<boolean>(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(true);
  const [insightLoading, setInsightLoading] = useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

  const userService = useMemo(() => new UserService(), []);

  // Check if this insight was previously dismissed
  useEffect(() => {
    const dismissedKey = `dismissed_insight_${insightType}_${dateId}`;
    const wasDismissed = safeGetItem(dismissedKey) === "true";
    setIsDismissed(wasDismissed);
  }, [insightType, dateId]);

  // When forceShow becomes true, clear dismissed state
  useEffect(() => {
    if (forceShow && isDismissed) {
      setIsDismissed(false);
    }
  }, [forceShow]);

  // Check user subscription status and insights enabled setting
  useEffect(() => {
    const checkSubscriptionAndSettings = async () => {
      if (!currentUser?.uid) {
        setSubscriptionLoading(false);
        return;
      }

      const [hasAccess, enabled] = await Promise.all([
        userService.canAccessPremium(currentUser.uid),
        getPersonalizedInsightsEnabled(currentUser.uid),
      ]);

      setHasAccess(hasAccess || false);
      setInsightsEnabled(enabled);
      setSubscriptionLoading(false);
    };
    checkSubscriptionAndSettings();
  }, [currentUser?.uid, userService]);

  // Check for cached insights and generate if needed
  useEffect(() => {
    const checkAndGenerateInsight = async () => {
      if (
        !currentUser?.uid ||
        isDismissed ||
        !hasAccess ||
        !insightsEnabled ||
        subscriptionLoading ||
        !hasRequiredData
      ) {
        setInsight("");
        setInsightLoading(false);
        return;
      }

      // First check for cached insights
      const cachedInsight = getCachedInsight(
        currentUser.uid,
        insightType,
        timeframe,
      );
      if (cachedInsight) {
        setInsight(cachedInsight);
        setInsightLoading(false);
        console.log(`🎯 Found cached ${insightType} insight for dashboard`);
        return;
      }

      // No cached insight found, generate new one
      console.log(
        `📭 No cached ${insightType} insight found, generating new one...`,
      );
      setInsightLoading(true);

      try {
        const newInsight = await generateInsights(
          currentUser.uid,
          insightType,
          timeframe,
        );
        if (newInsight) {
          setInsight(newInsight);
          console.log(`✅ Generated new ${insightType} insight for dashboard`);
        } else {
          setInsight("");
          console.log(`❌ Failed to generate ${insightType} insight`);
        }
      } catch (error) {
        console.error(`❌ Error generating ${insightType} insight:`, error);
        setInsight("");
      } finally {
        setInsightLoading(false);
      }
    };

    checkAndGenerateInsight();
  }, [
    currentUser?.uid,
    insightType,
    timeframe,
    isDismissed,
    hasAccess,
    insightsEnabled,
    subscriptionLoading,
    hasRequiredData,
  ]);

  const handleDismiss = () => {
    const dismissedKey = `dismissed_insight_${insightType}_${dateId}`;
    safeSetItem(dismissedKey, "true");
    setIsDismissed(true);

    // Call parent dismiss callback if provided
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleRegenerate = async () => {
    if (!currentUser?.uid || isRegenerating || insightLoading) {
      return;
    }

    setIsRegenerating(true);
    console.log(`🔄 Regenerating ${insightType} insight...`);

    try {
      // Generate new insight with bypassCache flag
      const newInsight = await generateInsights(
        currentUser.uid,
        insightType,
        timeframe,
        false, // waitForConsistency = false
        true, // bypassCache = true to force regeneration
      );

      if (newInsight) {
        setInsight(newInsight);
        console.log(`✅ Successfully regenerated ${insightType} insight`);
      } else {
        console.log(`❌ Failed to regenerate ${insightType} insight`);
      }
    } catch (error) {
      console.error(`❌ Error regenerating ${insightType} insight:`, error);
    } finally {
      setIsRegenerating(false);
    }
  };

  // Don't render anything until subscription check is complete
  if (subscriptionLoading) {
    return null;
  }

  if (!hasAccess) {
    return null;
  }

  // Don't render if insights are disabled by user
  if (!insightsEnabled) {
    return null;
  }

  // Don't render if dismissed
  if (isDismissed) {
    return null;
  }

  // Don't render if required data is not available
  if (!hasRequiredData) {
    return null;
  }

  // Don't render if no insight exists and not loading
  if (!insight && !insightLoading) {
    return null;
  }

  const getInsightTitle = () => {
    switch (insightType) {
      case "wellness":
        return "Personalised wellness insight";
      case "peak":
        return "Personalised peak insight";
      case "sleep":
        return "Personalised sleep insight";
      case "training":
        return "Personalised training insight";
      case "daily":
      default:
        return "Daily Insight";
    }
  };

  return (
    <div className="mx-auto pt-8 xl:px-2 mb-0 sm:mb-4">
      <div className="bg-base-300/50 backdrop-blur-lg sm:rounded-xl rounded-none p-4 pt-6 sm:pt-6 px-8 max-w-3xl mx-auto sm:border sm:border-base-100">
        <div className={`${className}`}>
          <div className="flex flex-col gap-3 items-start">
            <div className="flex gap-4 items-center justify-between w-full">
              <div className="flex gap-4 items-center">
                <SparklesIcon className="h-5 w-5 text-base-content/80" />
                <p className="text-md text-base-content font-bold font-geist">
                  {getInsightTitle()}
                </p>
                {/* Regenerate button */}
                <button
                  onClick={handleRegenerate}
                  disabled={isRegenerating || insightLoading}
                  className="p-1 hover:bg-blue-500/10 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={`Regenerate ${insightType} insight`}
                >
                  <ArrowPathIcon
                    className={`h-4 w-4 text-base-600 hover:text-base-700 dark:text-base-400 ${
                      isRegenerating ? "animate-spin" : ""
                    }`}
                  />
                </button>
              </div>
              <div className="flex gap-2 items-center">
                {/* Dismiss button */}
                <button
                  onClick={handleDismiss}
                  className="p-1 hover:bg-lime/40 rounded-full transition-colors flex-shrink-0"
                  title={`Dismiss ${insightType} insight for today`}
                >
                  <XMarkIcon className="h-4 w-4 text-base-content/80 hover:text-base-content" />
                </button>
              </div>
            </div>

            <div className="w-full">
              <div className="space-y-3 font-geist">
                {insightLoading || isRegenerating ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="loading loader-spinner loading-sm"></div>
                    <span className="ml-2 text-sm text-base-content/80 font-geist">
                      {isRegenerating
                        ? "Regenerating your personalized insight..."
                        : "Generating your personalized insight..."}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-base-content font-grotesk">
                    {insight}
                  </p>
                )}
                {onLearnMore && insight && !insightLoading && (
                  <div className="flex justify-end">
                    <button
                      onClick={onLearnMore}
                      disabled={learnMoreLoading}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-base-100  rounded-md text-base-content/80 hover:text-base-content hover:bg-base-100 font-grotesk font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {learnMoreLoading ? (
                        <>
                          <div className="loading loader-spinner loading-xs "></div>
                          <span>Starting conversation...</span>
                        </>
                      ) : (
                        <>
                          {/* <SparklesIcon className="h-3 w-3" /> */}
                          <span>Go deeper...</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardAIInsightCard;
